// components/LocationSelector/LocationSelector.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Autocomplete,
  Grid
} from '@mui/material';
import { MapPin, Locate, MapPinned } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { debounce } from '@mui/material/utils';
import parse from 'autosuggest-highlight/parse';
import { getGeo, getCachedLocation, formatLocation } from '../../utils/Geo/getGeo';
import { useUser } from '../../context/UserContext';

// Load Google Maps Script
function loadScript(src, position, id) {
  if (!position) return;  
  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

export const LocationSelector = ({ dialogMode = false, open: externalOpen = false, onClose: externalOnClose }) => {
  const { t, i18n } = useTranslation();
  const { getCity, updateLocation } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [internalOpen, setInternalOpen] = useState(false);
  
  // Determine which open state to use
  const isOpen = dialogMode ? externalOpen : internalOpen;
  
  const handleClose = () => {
    if (dialogMode && externalOnClose) {
      externalOnClose();
    } else {
      setInternalOpen(false);
    }
    setError('');
  };

  const [location, setLocation] = useState(() => {
    const cached = getCachedLocation();
    return formatLocation(cached);
  });
  
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loaded = useRef(false);

  // Get config from environment variables
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const APP_LANGUAGE = process.env.REACT_APP_LANGUAGE || 'en';
  const APP_COUNTRY = process.env.REACT_APP_COUNTRY || 'us';

  /**
   * Update postal code in URL params
   */
  const updatePostalCodeParam = (postalCode) => {
    if (!postalCode) return;
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pc', postalCode);
    setSearchParams(newParams, { replace: true });
  };

  // Load Google Maps Script
  useEffect(() => {
    if (typeof window !== 'undefined' && !loaded.current && GOOGLE_MAPS_API_KEY) {
      if (!document.querySelector('#google-maps')) {
        const language = i18n.language || APP_LANGUAGE;
        loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=${language}`,
          document.querySelector('head'),
          'google-maps'
        );
      }
      loaded.current = true;
    }
  }, [i18n.language, GOOGLE_MAPS_API_KEY, APP_LANGUAGE]);

  // Debounced fetch for autocomplete
  const fetch = useMemo(
    () =>
      debounce((request, callback) => {
        if (autocompleteService.current) {
          autocompleteService.current.getPlacePredictions(
            { 
              ...request, 
              componentRestrictions: { country: APP_COUNTRY }
            },
            callback
          );
        }
      }, 400),
    [APP_COUNTRY]
  );

  // Initialize autocomplete service and fetch predictions
  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
    }

    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results) => {
      if (active) {
        let newOptions = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const handleOpen = () => {
    setError('');
    setInternalOpen(true);
  };

  const handleSave = () => {
    if (value) {
      const locationString = value.description || value.structured_formatting?.main_text || '';
      setLocation(locationString);
      
      // Extract postal code from place details
      extractPlaceDetails(value.place_id, locationString);
      
      handleClose();
    }
  };

  const extractPlaceDetails = async (placeId, locationString) => {
    if (!window.google?.maps?.places) return;

    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );

    placesService.getDetails(
      {
        placeId: placeId,
        fields: ['address_components', 'geometry', 'formatted_address']
      },
      async (place, status) => {
        if (status === 'OK' && place) {
          const addressComponents = place.address_components;
          let postalCode = '';
          let city = '';
          let region = '';
          let street = '';
          let country = '';
          let countryCode = '';

          addressComponents.forEach(component => {
            if (component.types.includes('postal_code')) {
              postalCode = component.long_name;
            }
            if (component.types.includes('locality')) {
              city = component.long_name;
            }
            if (component.types.includes('administrative_area_level_1')) {
              region = component.long_name;
            }
            if (component.types.includes('route')) {
              street = component.long_name;
            }
            if (component.types.includes('country')) {
              country = component.long_name;
              countryCode = component.short_name;
            }
          });

          const addressData = {
            street: street || locationString,
            city: city || '',
            region: region || '',
            postalCode: postalCode || '',
            country: country || '',
            countryCode: countryCode || '',
            latitude: place.geometry?.location?.lat() || null,
            longitude: place.geometry?.location?.lng() || null,
            formatted: place.formatted_address || locationString,
            source: 'manual'
          };

          // Use the centralized updateLocation from UserContext
          try {
            await updateLocation(addressData);
            
            // Update URL params with postal code
            if (postalCode) {
              updatePostalCodeParam(postalCode);
            }
            
            console.log('Location updated successfully');
          } catch (error) {
            console.error('Failed to update location:', error);
            setError(t('location.saveFailed') || 'Failed to save location');
          }
        }
      }
    );
  };

  const handleDetectLocation = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await getGeo();
      
      if (result.success && result.location) {
        const locationString = formatLocation(result.location);
        setLocation(locationString);
        
        // Update the autocomplete with detected location
        if (result.location.formatted) {
          setValue({
            description: result.location.formatted,
            place_id: null
          });
          setInputValue(result.location.formatted);
        }

        // Save the detected location using centralized method
        const locationData = {
          street: result.location.street || '',
          city: result.location.city || '',
          region: result.location.region || '',
          postalCode: result.location.postalCode || '',
          country: result.location.country || '',
          countryCode: result.location.countryCode || '',
          latitude: result.location.latitude || null,
          longitude: result.location.longitude || null,
          formatted: result.location.formatted || locationString,
          source: result.location.source || 'detected'
        };

        await updateLocation(locationData);
        
        // Update URL params with postal code
        if (result.location.postalCode) {
          updatePostalCodeParam(result.location.postalCode);
        }
        
        // Close dialog if location was successfully detected via browser
        if (result.location.source === 'browser-geolocation') {
          handleClose();
        }
      } else {
        setError(t('location.error') || 'Could not detect location');
      }
    } catch (err) {
      console.error('Location detection error:', err);
      setError(t('location.error') || 'Failed to detect location');
    } finally {
      setLoading(false);
    }
  };

  // Show warning if API key is missing
  if (!GOOGLE_MAPS_API_KEY) {
    console.warn('Google Maps API key is missing. Please set REACT_APP_GOOGLE_MAPS_API_KEY in .env file');
  }

  return (
    <>
      {!dialogMode && (
        <Button
          color="inherit"
          startIcon={<MapPin size={18} />}
          onClick={handleOpen}
          sx={{
            textTransform: 'none',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' }
          }}
        >
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
              {t('location.deliverTo')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {getCity() || location || t('location.notSet')}
            </Typography>
          </Box>
        </Button>
      )}

      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('location.description')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              fullWidth
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.description
              }
              filterOptions={(x) => x}
              options={options}
              autoComplete
              includeInputInList
              filterSelectedOptions
              value={value}
              noOptionsText={t('location.noOptions') || 'No locations found'}
              onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
              }}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('location.enterLocation')}
                  placeholder={t('location.placeholder')}
                  disabled={loading}
                />
              )}
              renderOption={(props, option) => {
                const matches =
                  option.structured_formatting?.main_text_matched_substrings || [];

                const parts = parse(
                  option.structured_formatting?.main_text || option.description,
                  matches.map((match) => [match.offset, match.offset + match.length])
                );

                const { key, ...otherProps } = props;

                return (
                  <li key={key} {...otherProps}> 
                    <Grid container alignItems="center">
                      <Grid item sx={{ display: 'flex', width: 44 }}>
                        <MapPinned style={{ color: 'gray' }} />
                      </Grid>
                      <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                        {parts.map((part, index) => (
                          <Box
                            key={index}
                            component="span"
                            sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                          >
                            {part.text}
                          </Box>
                        ))}
                        <Typography variant="body2" color="text.secondary">
                          {option.structured_formatting?.secondary_text}
                        </Typography>
                      </Grid>
                    </Grid>
                  </li>
                );
              }}
            />
            
            <Button
              variant="outlined"
              onClick={handleDetectLocation}
              disabled={loading}
              sx={{ minWidth: 'auto', px: 2 }}
              title={t('location.detectLocation') || 'Detect location'}
            >
              {loading ? <CircularProgress size={20} /> : <Locate size={20} />}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t('location.detectHint') || 'Click location icon to auto-detect your location'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>{t('location.cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!value}
          >
            {t('location.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};